import React from 'react';
import {
    IconButtonAlignEdge,
    IconButtonColor,
    IconButtonShape,
    IconButtonSize,
    ResponsiveStyleValue,
    useComponentDefaultProps,
} from '../../theming';

import { ButtonBase, ButtonBaseProps } from '../ButtonBase/ButtonBase';
import { Styler, StylerProps } from '../Styler/Styler';

import { SvgIconProps } from '../SvgIcon/SvgIcon';
import { STATIC_STYLES, useDynamicStyles } from './IconButton.styles';

export interface IconButtonProps extends StylerProps, ButtonBaseProps {
    /**
     * Applies a negative margin equal to the button padding, allowing the
     * button icon to be aligned with other content.
     * @default none
     */
    alignEdge?: IconButtonAlignEdge;

    /**
     * The child icon component.
     */
    children: React.ReactElement<SvgIconProps>;

    /**
     * The colour variant of the button.
     * @default primary
     */
    color?: IconButtonColor;

    /**
     * The button's corner style. It might be preferable to set this on the theme using default props.
     * @default circle
     */
    shape?: IconButtonShape;

    /**
     * The text that will be used for the component's `aria-label` attribute.
     */
    label?: string;

    /**
     * The size of the button.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default m
     */
    size?: ResponsiveStyleValue<IconButtonSize>;

    /**
     * If `true`, the button will have a solid background.
     * @default false
     */
    filled?: boolean;
}

const defaultProps: Partial<IconButtonProps> = {
    color: 'primary',
    size: 'm',
    shape: 'circle',
};

/**
 * Icon Buttons are low-emphasis buttons that allow users to perform actions or make choices.
 * They are typically placed in mastheads or toolbars where space may be limited.
 *
 * These buttons should only be used when an icon alone is sufficient to convey the action it performs.
 * A label should always be provided for accessibility.
 */
export function IconButton(props: Readonly<IconButtonProps>): React.JSX.Element {
    const {
        children,
        alignEdge = 'none',
        disabled,
        color = 'primary',
        shape = 'circle',
        label,
        size = 'm',
        filled = false,
        ref,
        ...other
    } = useComponentDefaultProps('IconButton', defaultProps, props);

    const icon = React.Children.only(children);

    const cssVariables = useDynamicStyles(size, color);

    return (
        <Styler
            ref={ref}
            component={ButtonBase}
            style={cssVariables}
            baseStyles={STATIC_STYLES}
            aria-label={label}
            disabled={disabled}
            data-align-edge={alignEdge !== 'none' ? alignEdge : undefined}
            data-shape={shape}
            data-filled={filled || undefined}
            {...other}
        >
            {React.cloneElement(icon, {
                'data-icon': 'true',
                ...icon.props,
            })}
        </Styler>
    );
}
